"use client";

import {
  PortableText,
  PortableTextBlock,
  type PortableTextComponents,
} from "@portabletext/react";
import { CheckCircle2Icon, ChevronDownIcon, QuoteIcon } from "lucide-react";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import WistiaEmbed from "./WistiaEmbed";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Tweet } from "react-tweet";

function renderBullets(cellText: string) {
  const normalized = cellText.replace(/\\n/g, "\n");
  const lines = normalized
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const isBulletList = lines.every((line) => line.startsWith("•"));

  if (isBulletList) {
    return (
      <ul className="list-disc pl-5">
        {lines.map((line, i) => (
          <li key={i}>{line.replace(/^•\s?/, "")}</li>
        ))}
      </ul>
    );
  }

  return lines.map((line, i) => <p key={i}>{line}</p>);
}

const YouTubePlayer = dynamic(() => import("./YoutubePlayer"), { ssr: false });

export const components: PortableTextComponents = {
  types: {
    youtube: ({ value }) => <YouTubePlayer url={value?.url} />,
    image: ({ value }) => (
      <div className="w-full flex flex-col gap-1">
        <Image
          width={value?.asset?.metadata?.dimensions?.width ?? 700}
          height={value?.asset?.metadata?.dimensions?.height ?? 350}
          src={value?.asset?.url}
          alt={value?.alt}
        />
        <p className="text-sm lg:text-base text-[#0D2A4C99]">
          {value?.caption}
        </p>
      </div>
    ),
    promoCard: ({ value }) => (
      <div className="flex flex-col p-4 border border-[#2A8DFF] rounded-[12px] bg-[#2A8DFF]/5">
        <h2 className="text-xl text-[#0D2A4C] font-semibold">
          {value?.headline}
        </h2>
        <PortableText value={value?.body} components={components} />
        <div className="flex flex-col lg:flex-row gap-2">
          <Link
            className="bg-[#2A8DFF] hover:bg-[#2A8DFF]/90 active:outline-1 active:outline-[#2A8DFF] active:bg-[#2A8DFF] !cursor-pointer px-8 text-center lg:py-[18px] py-[14px] rounded-[100px] text-white font-semibold"
            href={value?.primaryButton?.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {value?.primaryButton?.label}
          </Link>
          {!!value?.secondaryButton && (
            <Link
              className="bg-white cursor-pointer hover:bg-[#F8F9FA] active:outline-1 active:outline-white active:bg-white w-full text-center sm:w-fit px-8 lg:py-[18px] py-[14px] border border-[#0D2A4C08] rounded-[100px] text-[#0D2A4C] font-semibold"
              href={value?.secondaryButton?.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {value?.secondaryButton?.label}
            </Link>
          )}
        </div>
      </div>
    ),
    listFeaturesBlock: ({ value }) => (
      <div className="flex flex-col py-4 px-6 border border-[#2A8DFF] rounded-[12px] bg-[#2A8DFF]/10">
        {!!value?.heading && (
          <h2 className="text-xl text-[#0D2A4C] font-semibold">
            {value?.heading}
          </h2>
        )}
        <div className="flex flex-col gap-2">
          {value?.items?.map(
            (item: { _key: string; body: Array<PortableTextBlock> }) => (
              <div key={item._key} className="flex gap-2 items-center">
                <CheckCircle2Icon className="text-[#2A8DFF] size-6" />
                <div className="flex-1">
                  <PortableText value={item.body} components={components} />
                </div>
              </div>
            )
          )}
        </div>
      </div>
    ),
    wistiaVideo: ({ value }) => (
      <div className="w-full">
        <WistiaEmbed url={value?.url} />
      </div>
    ),
    quoteBox: ({ value }) => (
      <div className="relative flex flex-col py-4 px-6 border border-[#2A8DFF] rounded-[12px] bg-[#2A8DFF]/10">
        {!!value?.heading && (
          <h2 className="text-xl text-[#0D2A4C] font-semibold">
            {value?.heading}
          </h2>
        )}
        <PortableText value={value?.body} components={components} />
        <QuoteIcon className="text-[#2A8DFF] absolute top-2 right-2" />
      </div>
    ),
    table: ({ value }) => (
      <div className="w-full overflow-x-auto">
        <table>
          <thead>
            <tr className="bg-[#2A8DFF] text-sm font-semibold text-white rounded-t-[12px]">
              {value?.rows[0]?.cells?.map((cell: string, index: number) => (
                <td
                  key={`${cell}-${index}`}
                  className="border border-[#0D2A4C33] p-3"
                >
                  {cell}
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {value?.rows
              ?.slice(1)
              ?.map((row: { _key: string; cells: Array<string> }) => (
                <tr key={row._key}>
                  {row?.cells?.map((cell: string, index: number) => (
                    <td
                      key={`${cell}-${index}`}
                      className="border border-[#0D2A4C33] text-[#0D2A4C] p-3 text-sm"
                    >
                      {renderBullets(cell)}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    ),
    collapsible: ({ value }) => (
      <div className="flex flex-col gap-2">
        {value?.items?.map(
          (item: {
            _key: string;
            title: string;
            body: Array<PortableTextBlock>;
          }) => (
            <Collapsible
              key={item._key}
              className="bg-[#F8F9FA] w-full border border-[#0D2A4C08] rounded-[100px] py-[22px] px-7 data-[state=open]:rounded-[12px] data-[state=open]:[&>button>svg:nth-of-type(1)]:rotate-180"
            >
              <CollapsibleTrigger className="w-full flex justify-between items-center cursor-pointer">
                <p className="text-[#0D2A4C] tracking-tight lg:text-[16px] lg:leading-4 font-semibold text-start">
                  {item.title}
                </p>
                <ChevronDownIcon className="lg:size-[22px]" />
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3.5 lg:pt-4">
                <PortableText value={item?.body} components={components} />
              </CollapsibleContent>
            </Collapsible>
          )
        )}
      </div>
    ),
    opinionBox: ({ value }) => (
      <div className="relative my-4 flex flex-col py-4 pr-6 pl-12 border border-[#2A8DFF] rounded-[12px] bg-white shadow-lg">
        {value?.url ? (
          <Link href={value?.url}>
            <h2 className="text-xl font-semibold text-[#2A8DFF] hover:text-blue-600">
              {value?.heading}
            </h2>
          </Link>
        ) : (
          <h2 className="text-xl font-semibold text-[#0D2A4C]">
            {value?.heading}
          </h2>
        )}

        <PortableText value={value?.body} components={components} />
        <div className="flex flex-col gap-1">
          <p className="lg:text-base text-sm text-[#0D2A4C99] font-semibold">
            {value?.author}
          </p>
          <p className="lg:text-sm text-xs text-[#0D2A4C99]">
            {value?.authorDescription}
          </p>
        </div>
        <QuoteIcon className="text-[#2A8DFF] absolute top-2 left-2 scale-x-[-1]" />
      </div>
    ),
    successMetrics: ({ value }) => (
      <div className="w-full rounded-[12px] bg-[#2A8DFF]/10 p-4 flex flex-col items-center gap-6 my-4">
        <p className="text-3xl text-[#0D2A4C] font-semibold">
          Success <span className="text-[#2A8DFF]">Metrics</span>
        </p>
        <div className="flex flex-col lg:flex-row gap-6">
          {value?.items?.map(
            (item: { heading: string; description: string }, index: number) => (
              <div
                key={`${item.heading}-${index}`}
                className="flex-1 flex flex-col items-center text-center"
              >
                <p className="text-2xl text-[#0D2A4C] font-semibold">
                  {item.heading}
                </p>
                <p>{item.description}</p>
              </div>
            )
          )}
        </div>
      </div>
    ),
    twitter: ({ value }) => {
      const tweetId = value.tweetUrl?.split("/").pop();
      if (!tweetId) return null;
      return <Tweet id={tweetId} />;
    },
    linkedin: ({ value }) => {
      if (!value?.postUrl) return null;
      return (
        <div className="my-6 w-full max-w-[600px]">
          <iframe
            loading="lazy"
            src={value.postUrl}
            height={600}
            width="100%"
            allowFullScreen
            title="LinkedIn Post"
          ></iframe>
        </div>
      );
    },
  },
  block: {
    h1: ({ children }) => (
      <h1 className="text-3xl text-[#0D2A4C] font-bold my-6">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-2xl text-[#0D2A4C] font-semibold my-5">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-xl text-[#0D2A4C] font-semibold my-4">{children}</h3>
    ),
    normal: ({ children }) => (
      <p className="lg:text-base text-sm !text-[#0D2A4C99] my-4">{children}</p>
    ),
    blockquote: ({ children }) => (
      <blockquote className="bg-[#ECF4FD] py-5 lg:py-7 px-7 lg:px-10 my-4 text-[#0D2A4C99]">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="list-disc pl-5 my-4 text-[#0D2A4C99] text-sm lg:text-base">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="list-decimal pl-5 my-4 text-[#0D2A4C99] text-sm lg:text-base">
        {children}
      </ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => <li className="mb-1">{children}</li>,
    number: ({ children }) => <li className="mb-1">{children}</li>,
  },
  marks: {
    strong: ({ children }) => (
      <strong className="font-semibold">{children}</strong>
    ),
    em: ({ children }) => <em className="italic">{children}</em>,
    code: ({ children }) => (
      <code className="bg-gray-100 rounded px-1 py-0.5 font-mono text-sm">
        {children}
      </code>
    ),
    link: ({ children, value }) => (
      <a
        href={value?.href}
        target={value?.blank ? "_blank" : "_self"}
        rel="noopener noreferrer"
        className="underline text-[#2A8DFF] hover:text-blue-500"
      >
        {children}
      </a>
    ),
  },
};

export default function ClientPortableText({
  value,
}: {
  value: Array<PortableTextBlock>;
}) {
  return <PortableText value={value} components={components} />;
}
